// Pure (no Firebase imports) weekly-schedule engine for recurring raffles. Kept free of any
// I/O so it can be unit tested exhaustively, including DST transitions. The admin portal ships
// an identical copy (`winfacil-admin/src/components/raffle/raffleSchedule.ts`) so the form can
// preview the next publish date without a server round-trip - keep both files in sync.

// ISO weekday numbers: 1 = Monday ... 7 = Sunday.
export type IsoWeekday = 1 | 2 | 3 | 4 | 5 | 6 | 7

export type WeeklySchedule = {
  enabled?: boolean
  paused?: boolean
  weekdays?: number[]
  // "HH:mm" in `timezone`.
  publishTime?: string
  // "HH:mm" in `timezone`. When <= publishTime the window ends on the following day.
  endTime?: string
  // IANA zone, e.g. "America/New_York".
  timezone?: string
  // "YYYY-MM-DD" in `timezone`, first date an occurrence may start on.
  startDate?: string
  repeatIndefinitely?: boolean
  // "YYYY-MM-DD" in `timezone`, last date an occurrence may start on. Ignored while
  // `repeatIndefinitely` is true.
  endDate?: string
}

export type ScheduleOccurrence = {
  // "YYYY-MM-DD" local date the run starts on - unique per schedule, used for idempotency.
  key: string
  publishAt: number
  endAt: number
}

export type ScheduleStatus = "off" | "invalid" | "paused" | "active" | "finished"

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/
const DAY_MS = 24 * 60 * 60 * 1000

export const isValidTimeZone = (timeZone: unknown): timeZone is string => {
  if (typeof timeZone !== "string" || !timeZone.trim()) return false
  try {
    new Intl.DateTimeFormat("en-US", { timeZone })
    return true
  } catch {
    return false
  }
}

const parseDateKey = (value: unknown): { y: number; m: number; d: number } | null => {
  if (typeof value !== "string") return null
  const match = DATE_PATTERN.exec(value)
  if (!match) return null
  const y = Number(match[1])
  const m = Number(match[2])
  const d = Number(match[3])
  const probe = new Date(Date.UTC(y, m - 1, d))
  // Rejects rollovers such as 2026-02-31.
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) {
    return null
  }
  return { y, m, d }
}

const parseTime = (value: unknown): { h: number; min: number } | null => {
  if (typeof value !== "string") return null
  const match = TIME_PATTERN.exec(value)
  return match ? { h: Number(match[1]), min: Number(match[2]) } : null
}

const pad = (value: number) => String(value).padStart(2, "0")

const formatDateKey = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`

const addDaysToDateKey = (dateKey: string, days: number): string => {
  const parsed = parseDateKey(dateKey)!
  const next = new Date(Date.UTC(parsed.y, parsed.m - 1, parsed.d) + days * DAY_MS)
  return formatDateKey(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate())
}

const isoWeekdayOfDateKey = (dateKey: string): number => {
  const parsed = parseDateKey(dateKey)!
  const jsDay = new Date(Date.UTC(parsed.y, parsed.m - 1, parsed.d)).getUTCDay()
  return jsDay === 0 ? 7 : jsDay
}

const formatterCache = new Map<string, Intl.DateTimeFormat>()
const getFormatter = (timeZone: string) => {
  let formatter = formatterCache.get(timeZone)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
    formatterCache.set(timeZone, formatter)
  }
  return formatter
}

const getZonedParts = (utcMs: number, timeZone: string) => {
  const parts: Record<string, number> = {}
  for (const part of getFormatter(timeZone).formatToParts(new Date(utcMs))) {
    if (part.type !== "literal") parts[part.type] = Number(part.value)
  }
  // Some engines report midnight as hour 24 even with h23.
  if (parts.hour === 24) parts.hour = 0
  return parts
}

// Offset (ms) of `timeZone` from UTC at the instant `utcMs` (positive east of UTC).
export const getTimeZoneOffsetMs = (utcMs: number, timeZone: string): number => {
  const p = getZonedParts(utcMs, timeZone)
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return asUtc - Math.floor(utcMs / 1000) * 1000
}

// Local "YYYY-MM-DD" of the instant `utcMs` in `timeZone`.
export const getLocalDateKey = (utcMs: number, timeZone: string): string => {
  const p = getZonedParts(utcMs, timeZone)
  return formatDateKey(p.year, p.month, p.day)
}

// Converts a wall-clock date+time in `timeZone` to a UTC instant, DST aware. A wall time that
// does not exist (spring-forward gap) resolves to the first valid instant after the gap; an
// ambiguous wall time (fall-back overlap) resolves to its first (earlier) occurrence.
export const zonedTimeToUtc = (dateKey: string, time: string, timeZone: string): number => {
  const date = parseDateKey(dateKey)!
  const t = parseTime(time)!
  const naive = Date.UTC(date.y, date.m - 1, date.d, t.h, t.min, 0)
  const offsetBefore = getTimeZoneOffsetMs(naive - DAY_MS, timeZone)
  const offsetAfter = getTimeZoneOffsetMs(naive + DAY_MS, timeZone)

  const candidates = Array.from(new Set([offsetBefore, offsetAfter])).map((offset) => naive - offset)
  const valid = candidates.filter((candidate) => {
    const p = getZonedParts(candidate, timeZone)
    return (
      p.year === date.y &&
      p.month === date.m &&
      p.day === date.d &&
      p.hour === t.h &&
      p.minute === t.min
    )
  })
  if (valid.length > 0) return Math.min(...valid)

  // Gap: shift forward by the size of the gap, i.e. use the pre-transition offset.
  return naive - offsetBefore
}

export const validateWeeklySchedule = (schedule: WeeklySchedule | null | undefined): string[] => {
  const errors: string[] = []
  if (!schedule || typeof schedule !== "object") return ["Schedule is missing"]

  const weekdays = Array.isArray(schedule.weekdays) ? schedule.weekdays : []
  if (
    weekdays.length === 0 ||
    weekdays.some((day) => !Number.isInteger(day) || day < 1 || day > 7)
  ) {
    errors.push("Select at least one weekday")
  }
  if (!parseTime(schedule.publishTime)) errors.push("Publish time is required (HH:mm)")
  if (!parseTime(schedule.endTime)) errors.push("End time is required (HH:mm)")
  if (parseTime(schedule.publishTime) && parseTime(schedule.endTime)) {
    if (schedule.publishTime === schedule.endTime) {
      errors.push("End time must be different from the publish time")
    }
  }
  if (!isValidTimeZone(schedule.timezone)) errors.push("A valid time zone is required")
  if (!parseDateKey(schedule.startDate)) errors.push("Start date is required")
  if (!schedule.repeatIndefinitely) {
    if (!parseDateKey(schedule.endDate)) {
      errors.push("End date is required unless the schedule repeats indefinitely")
    } else if (
      parseDateKey(schedule.startDate) &&
      (schedule.endDate as string) < (schedule.startDate as string)
    ) {
      errors.push("End date must not be before the start date")
    }
  }
  return errors
}

const buildOccurrence = (schedule: WeeklySchedule, dateKey: string): ScheduleOccurrence => {
  const timeZone = schedule.timezone as string
  const publishAt = zonedTimeToUtc(dateKey, schedule.publishTime as string, timeZone)
  const endsNextDay = (schedule.endTime as string) <= (schedule.publishTime as string)
  const endDateKey = endsNextDay ? addDaysToDateKey(dateKey, 1) : dateKey
  const endAt = zonedTimeToUtc(endDateKey, schedule.endTime as string, timeZone)
  return { key: dateKey, publishAt, endAt }
}

const isDateKeyInRange = (schedule: WeeklySchedule, dateKey: string) => {
  if (dateKey < (schedule.startDate as string)) return false
  if (!schedule.repeatIndefinitely && dateKey > (schedule.endDate as string)) return false
  return true
}

const occurrenceForDateKey = (
  schedule: WeeklySchedule,
  dateKey: string,
): ScheduleOccurrence | null => {
  if (!isDateKeyInRange(schedule, dateKey)) return null
  if (!(schedule.weekdays as number[]).includes(isoWeekdayOfDateKey(dateKey))) return null
  return buildOccurrence(schedule, dateKey)
}

// Latest occurrence whose publishAt is at or before `now` (it may or may not still be running).
export const getLatestStartedOccurrence = (
  schedule: WeeklySchedule,
  now: number,
): ScheduleOccurrence | null => {
  if (validateWeeklySchedule(schedule).length > 0) return null
  const today = getLocalDateKey(now, schedule.timezone as string)
  for (let offset = 1; offset >= -8; offset -= 1) {
    const occurrence = occurrenceForDateKey(schedule, addDaysToDateKey(today, offset))
    if (occurrence && occurrence.publishAt <= now) return occurrence
  }
  return null
}

// First occurrence strictly after `now`, or null when the schedule has no more runs.
export const getNextOccurrence = (
  schedule: WeeklySchedule,
  now: number,
): ScheduleOccurrence | null => {
  if (validateWeeklySchedule(schedule).length > 0) return null
  const today = getLocalDateKey(now, schedule.timezone as string)
  let startOffset = -1
  // Jump straight to the schedule start instead of scanning from far in the past.
  if ((schedule.startDate as string) > today) {
    startOffset = Math.round(
      (Date.parse(`${schedule.startDate}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / DAY_MS,
    )
  }
  for (let offset = startOffset; offset <= startOffset + 9; offset += 1) {
    const occurrence = occurrenceForDateKey(schedule, addDaysToDateKey(today, offset))
    if (occurrence && occurrence.publishAt > now) return occurrence
  }
  // An indefinite schedule always finds one within 8 days; a bounded one may be exhausted.
  if (!schedule.repeatIndefinitely) return null
  return null
}

export const getScheduleStatus = (
  schedule: WeeklySchedule | null | undefined,
  now: number,
): ScheduleStatus => {
  if (!schedule || schedule.enabled !== true) return "off"
  if (validateWeeklySchedule(schedule).length > 0) return "invalid"
  if (schedule.paused === true) return "paused"
  const current = getLatestStartedOccurrence(schedule, now)
  if (current && now < current.endAt) return "active"
  return getNextOccurrence(schedule, now) ? "active" : "finished"
}

export type ScheduleAction =
  | { type: "none" }
  | { type: "publish"; occurrence: ScheduleOccurrence }
  | { type: "hide"; occurrenceKey: string }

export type ScheduleRunState = {
  // Occurrence key currently published by this scheduler (hidden once its window ends).
  visibleKey?: string | null
  visibleEndAt?: number | null
  // Last occurrence key this scheduler published; guards against a second publish.
  lastPublishedKey?: string | null
}

// Decides what a single scheduler tick should do for one raffle. Pure and deterministic, so a
// re-run (retry, overlapping tick, late tick) with the same state yields the same action and the
// caller's idempotency check turns duplicates into no-ops.
export const decideScheduleAction = (
  schedule: WeeklySchedule,
  state: ScheduleRunState,
  now: number,
): ScheduleAction => {
  if (state.visibleKey && typeof state.visibleEndAt === "number" && now >= state.visibleEndAt) {
    return { type: "hide", occurrenceKey: state.visibleKey }
  }

  const current = getLatestStartedOccurrence(schedule, now)
  if (!current || now >= current.endAt) return { type: "none" }
  if (state.lastPublishedKey === current.key) return { type: "none" }
  return { type: "publish", occurrence: current }
}
